import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { withStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Chip from "@material-ui/core/Chip";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Slider from "@material-ui/core/Slider";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";

import brandLogoUrls, { normalizeBrandLogoKey } from "../common/logoUrls";
import { formatIls } from "./tirePairModel";
import { getVisibleListFilterSections } from "./tireListFilters";

const TYPE_OPTIONS = [
  { value: "all", label: "הכל", countKey: "totalCount" },
  { value: "single", label: "צמיגים בודדים", countKey: "singleCount" },
  { value: "set", label: "סטים", countKey: "setCount" },
];

const styles = (theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(2),
  },
  plain: {
    backgroundColor: "transparent",
    border: "none",
    borderRadius: 0,
    padding: 0,
  },
  section: {
    marginBottom: theme.spacing(2.5),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  divider: {
    marginBottom: theme.spacing(1.5),
  },
  segment: {
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: "hidden",
  },
  segmentButton: {
    borderRadius: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: "background-color 0.15s ease-in-out",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  segmentLabel: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  switchStack: {
    display: "flex",
    flexDirection: "column",
  },
  priceLabels: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: theme.spacing(1),
  },
  rimRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
  },
  brandRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    boxSizing: "border-box",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(0.5),
    backgroundColor: theme.palette.background.paper,
    cursor: "pointer",
    transition: "background-color 0.15s ease-in-out, border-color 0.15s ease-in-out",
    "&:last-child": {
      marginBottom: 0,
    },
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  brandRowSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
  brandCheck: {
    padding: theme.spacing(1),
  },
  brandMain: {
    flex: "1 1 auto",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
  },
  brandLogo: {
    display: "block",
    maxWidth: 92,
    maxHeight: 22,
    objectFit: "contain",
  },
});

class TireListFiltersPanel extends React.Component {
  handleBrandToggle = (brand) => {
    const key = String(brand || "").trim().toLowerCase();
    const current = this.props.filters.brands || [];
    const next = current.indexOf(key) === -1
      ? current.concat(key)
      : current.filter((item) => item !== key);

    this.props.onChange({ brands: next });
  };

  handleRimToggle = (rim) => {
    const key = String(rim || "").trim();
    const current = this.props.filters.rims || [];
    const next = current.indexOf(key) === -1
      ? current.concat(key)
      : current.filter((item) => item !== key);

    this.props.onChange({ rims: next });
  };

  handleStockToggle = (value) => (event) => {
    const current = this.props.filters.stock || [];
    const next = event.target.checked
      ? (current.indexOf(value) === -1 ? current.concat(value) : current)
      : current.filter((item) => item !== value);

    this.props.onChange({ stock: next });
  };

  handlePriceChange = (event, value) => {
    if (this.props.onPriceChange) {
      this.props.onPriceChange(value);
    }
  };

  handlePriceCommitted = (event, value) => {
    if (this.props.onPriceCommitted) {
      this.props.onPriceCommitted(value);
    }
  };

  renderSection(title, children) {
    const { classes } = this.props;

    return (
      <section className={classes.section}>
        <Typography variant="subtitle2" component="h2" gutterBottom>
          {title}
        </Typography>
        <Divider className={classes.divider} />
        {children}
      </section>
    );
  }

  renderType() {
    const { classes, facets, filters } = this.props;

    return this.renderSection(
      "סוג",
      <div className={classes.segment} role="group" aria-label="סוג">
        {TYPE_OPTIONS.map((option) => {
          const selected = filters.type === option.value;
          return (
            <Button
              key={option.value}
              fullWidth
              disableElevation
              variant={selected ? "contained" : "text"}
              color={selected ? "primary" : "default"}
              className={classes.segmentButton}
              classes={{ label: classes.segmentLabel }}
              onClick={() => this.props.onChange({ type: option.value })}
              aria-pressed={selected}
            >
              <span>{option.label}</span>
              <Typography
                variant="caption"
                component="span"
                color={selected ? "inherit" : "textSecondary"}
              >
                {facets[option.countKey] || 0}
              </Typography>
            </Button>
          );
        })}
      </div>
    );
  }

  renderAvailability(sections) {
    const { classes, facets, filters } = this.props;
    const stock = filters.stock || [];

    return this.renderSection(
      "זמינות",
      <div className={classes.switchStack}>
        {facets.localCount > 0 ? (
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={stock.indexOf("local") !== -1}
                onChange={this.handleStockToggle("local")}
              />
            }
            label={
              <Typography variant="body2" component="span">
                {`במלאי בארץ (${facets.localCount})`}
              </Typography>
            }
          />
        ) : null}
        {facets.euCount > 0 ? (
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={stock.indexOf("eu") !== -1}
                onChange={this.handleStockToggle("eu")}
              />
            }
            label={
              <Typography variant="body2" component="span">
                {`מלאי אירופה (${facets.euCount})`}
              </Typography>
            }
          />
        ) : null}
        {sections.freeShipping ? (
          <FormControlLabel
            control={
              <Switch
                color="primary"
                checked={!!filters.freeShippingOnly}
                onChange={(event) => this.props.onChange({ freeShippingOnly: event.target.checked })}
              />
            }
            label={
              <Typography variant="body2" component="span">
                {`משלוח חינם בלבד (${facets.freeShippingCount})`}
              </Typography>
            }
          />
        ) : null}
      </div>
    );
  }

  renderPrice() {
    const { classes, facets, sliderValue } = this.props;
    if (!sliderValue) {
      return null;
    }

    return this.renderSection(
      "מחיר",
      <React.Fragment>
        <div className={classes.priceLabels}>
          <Typography variant="body2">{formatIls(sliderValue[0])}</Typography>
          <Typography variant="body2">{formatIls(sliderValue[1])}</Typography>
        </div>
        <Slider
          min={facets.priceMin}
          max={facets.priceMax}
          step={1}
          value={sliderValue}
          onChange={this.handlePriceChange}
          onChangeCommitted={this.handlePriceCommitted}
          valueLabelDisplay="off"
          color="primary"
          getAriaLabel={(index) => (index === 0 ? "מחיר מינימום" : "מחיר מקסימום")}
        />
      </React.Fragment>
    );
  }

  renderRims() {
    const { classes, facets, filters } = this.props;
    const selected = filters.rims || [];

    return this.renderSection(
      "קוטר חישוק",
      <div className={classes.rimRow}>
        {facets.rims.map((item) => {
          const checked = selected.indexOf(item.rim) !== -1;
          return (
            <Chip
              key={item.rim}
              size="small"
              color={checked ? "primary" : "default"}
              variant={checked ? "default" : "outlined"}
              label={(
                <React.Fragment>
                  <bdi dir="ltr">{`${item.rim}"`}</bdi>
                  {` (${item.count})`}
                </React.Fragment>
              )}
              aria-pressed={checked}
              onClick={() => this.handleRimToggle(item.rim)}
            />
          );
        })}
      </div>
    );
  }

  renderBrands() {
    const { classes, facets, filters } = this.props;
    const selected = filters.brands || [];

    return this.renderSection(
      "מותג",
      <div>
        {facets.brands.map((item) => {
          const checked = selected.indexOf(item.key) !== -1;
          const logo = brandLogoUrls[normalizeBrandLogoKey(item.label)];

          return (
            <label
              key={item.key}
              className={classNames(classes.brandRow, checked && classes.brandRowSelected)}
            >
              <Checkbox
                color="primary"
                checked={checked}
                onChange={() => this.handleBrandToggle(item.key)}
                className={classes.brandCheck}
              />
              <span className={classes.brandMain}>
                {logo ? (
                  <img src={logo} alt={item.label} className={classes.brandLogo} />
                ) : (
                  <Typography variant="body2">{item.label}</Typography>
                )}
              </span>
              <Typography variant="caption" color="textSecondary">
                {item.count}
              </Typography>
            </label>
          );
        })}
      </div>
    );
  }

  render() {
    const { classes, variant, sliderValue } = this.props;
    const sections = getVisibleListFilterSections(this.props.facets);

    if (!sections.any) {
      return null;
    }

    return (
      <div className={classNames(classes.root, variant === "plain" && classes.plain)}>
        {sections.type ? this.renderType() : null}
        {sections.stock || sections.freeShipping ? this.renderAvailability(sections) : null}
        {sections.price && sliderValue ? this.renderPrice() : null}
        {sections.rims ? this.renderRims() : null}
        {sections.brands ? this.renderBrands() : null}
      </div>
    );
  }
}

TireListFiltersPanel.propTypes = {
  classes: PropTypes.object.isRequired,
  filters: PropTypes.shape({
    type: PropTypes.string,
    stock: PropTypes.arrayOf(PropTypes.string),
    freeShippingOnly: PropTypes.bool,
    brands: PropTypes.arrayOf(PropTypes.string),
    rims: PropTypes.arrayOf(PropTypes.string),
    minPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    maxPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  }).isRequired,
  facets: PropTypes.object.isRequired,
  sliderValue: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func.isRequired,
  onPriceChange: PropTypes.func,
  onPriceCommitted: PropTypes.func,
  variant: PropTypes.oneOf(["card", "plain"]),
};

TireListFiltersPanel.defaultProps = {
  sliderValue: null,
  variant: "card",
};

export default withStyles(styles)(TireListFiltersPanel);
